import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, CircularProgress, IconButton, Modal } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteQRCodeFolderById } from '~/api/qrCodeFolders';
import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import { folderKeys, qrCodeKeys } from '~/constants/queryKeys';
import { COLORS } from '~/styles/colors';

type DeleteFolderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  folderId: number;
};

export default function DeleteFolderModal({ isOpen, onClose, folderId }: DeleteFolderModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const styles = useStyles();

  const { mutate: deleteQRCodeFolderMutate, isPending: isPendingDeleteQRCodeFolder } = useMutation({
    mutationFn: deleteQRCodeFolderById,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qrCodeKeys.myQrsData }),
        queryClient.invalidateQueries({ queryKey: folderKeys.allFolders }),
      ]);

      onClose();
    },
  });

  return (
    <Modal
      open={isOpen}
      sx={styles.modal}
      slotProps={{
        backdrop: {
          sx: styles.backdrop,
        },
      }}
    >
      <Box sx={styles.container}>
        <IconButton onClick={onClose} sx={styles.closeIconWrapper}>
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>

        <Text variant="h2" sx={styles.title}>
          {t('folders:deleteFolderQuestion')}
        </Text>

        <Box sx={styles.bottomActions}>
          <ButtonBase sx={styles.bottomBtn} onClick={onClose} disableRipple>
            {t('buttons:cancel')}
          </ButtonBase>
          <Button
            onClick={() => deleteQRCodeFolderMutate(folderId)}
            disableRipple
            endIcon={isPendingDeleteQRCodeFolder ? <CircularProgress size={20} color="inherit" /> : undefined}
            size="small"
            sx={styles.deleteBtn}
          >
            {t('buttons:delete')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

function useStyles() {
  return {
    backdrop: css`
      background-color: #c9c8c84d;
      backdrop-filter: blur(3px);
    `,
    modal: css`
      display: flex;
      align-items: center;
      justify-content: center;
    `,
    closeIconWrapper: css`
      margin-left: auto;
    `,
    container: css`
      width: 100%;
      max-width: 475px;
      padding: 20px 32px 40px;
      border-radius: 20px;
      background-color: ${COLORS.white};
      display: flex;
      flex-direction: column;
    `,
    closeIcon: css`
      color: ${COLORS.textMain};
    `,
    title: css`
      cursor: initial;
      text-align: center;
      max-width: 334px;
      margin: 0 auto 24px;
    `,
    bottomBtn: css`
      height: 40px;
      padding: 4px 20px;
      box-sizing: border-box;
      font-weight: 500;
      font-size: 18px;
      color: ${COLORS.textMain};
      width: fit-content;
    `,
    deleteBtn: css`
      height: 40px;
      padding: 4px 20px !important;
      background-color: ${COLORS.darkRed} !important;
    `,
    bottomActions: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 32px;
    `,
  };
}
